import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { addMinutes, endOfDay, fromUnixTime, isToday, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import { getIdFromURN } from 'garaje/models/generic-resource';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { AvailabilityStatus } from 'garaje/types/maps/live-map/LiveMapServiceV2';
import type {
  Amenity,
  Floor,
  LiveMapResponse,
  Neighborhood,
  ProfileData,
  Resource,
  ResourceType,
} from 'garaje/types/maps/live-map/LiveMapServiceV2';
import urlBuilder from 'garaje/utils/url-builder';

const POLLING_INTERVAL_MINUTES = 0.5;
// This will be used to calculate the endTime from the current time when making a request for resource states
const CURRENT_STATE_INTERVAL_MINUTES = 5;

interface EmployeeMap {
  withReservations: { [id: string]: ProfileData };
  withAssignments: { [id: string]: ProfileData };
}

type ResourceTypeId = string;

// time fields are of RFC3339 format which is basically the same as ISO8601
export type LiveMapQueryFilters =
  | { floorId?: string; locationId?: string; areaMapId?: never; selectedTime?: number }
  | { floorId?: string; locationId?: never; areaMapId?: string; selectedTime?: number };

// This service is responsible for communicating with the live map endpoint to get the current state of resources
// And fetching the static data used in the live map view.
// It fetches and stores live map data in the form of resource states and polls the endpoint every 5 minutes.
// It also fetches and loads the static map data into the ember store
export default class LiveMapService extends Service {
  @service declare ajax: AjaxService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare router: RouterService;

  // stored values
  @tracked resources: Resource[] = [];
  @tracked resourceTypes: ResourceType[] = [];
  @tracked floors: Floor[] = [];
  @tracked neighborhoods: Neighborhood[] = [];
  @tracked amenities: Amenity[] = [];
  @tracked employeesMap: EmployeeMap = { withAssignments: {}, withReservations: {} };
  @tracked companyName: string | null = null;
  @tracked draftName: string | null = null;

  @tracked didLiveUpdateFail: boolean = false;
  @tracked currentPollingLocationId: string | null = null;
  @tracked selectedStartTime: string = '';

  @tracked externalShareToken: string | undefined = undefined;

  private _resourceTypeEnum: Record<string, string> = {};

  // Config for polling
  timeoutId: ReturnType<typeof setTimeout> | null = null; // TS normally expect a Timeout object, but in ember this is a number so this just tells TS to check the environment

  _toOfficeLocationTime(date: Date): Date {
    return this.state.getOfficeLocationTime(date);
  }

  _getStartEndTime(filters?: LiveMapQueryFilters): { startTime: string; endTime: string } {
    const selectedTime = filters?.selectedTime || this.router.currentRoute?.queryParams['selectedTime'];

    if (selectedTime && !isToday(fromUnixTime(Number(selectedTime)))) {
      return {
        startTime: this._toOfficeLocationTime(startOfDay(fromUnixTime(Number(selectedTime)))).toISOString(),
        endTime: this._toOfficeLocationTime(endOfDay(fromUnixTime(Number(selectedTime)))).toISOString(),
      };
    }

    const startTime = toZonedTime(new Date(), this.state.currentLocation.timezone);
    const endTime = addMinutes(startTime, CURRENT_STATE_INTERVAL_MINUTES);

    return {
      startTime: this._toOfficeLocationTime(startTime).toISOString(),
      endTime: this._toOfficeLocationTime(endTime).toISOString(),
    };
  }

  // Get all the resource states for current time
  fetchAndSaveLiveMapData = task({ drop: true }, async (filters?: LiveMapQueryFilters) => {
    const { startTime, endTime } = this._getStartEndTime(filters);

    const queryFilters = {
      floorId: filters?.floorId,
      locationId: filters?.locationId || this.state.currentLocation?.id,
      startTime: encodeURIComponent(startTime),
      endTime: encodeURIComponent(endTime),
    };

    this.currentPollingLocationId = queryFilters.locationId || null;

    const liveMapDataEndpoint = urlBuilder.maps.getLiveMapV2Url(queryFilters);
    const payload: LiveMapResponse = await this.ajax.request(liveMapDataEndpoint, {
      type: 'GET',
      headers: { Accept: 'application/vnd.api+json' },
    });

    this.resources = payload.resources;
    this.resourceTypes = payload.resourceTypes;
    this.floors = payload.floors;
    this.neighborhoods = payload.neighborhoods;
    this.amenities = payload.amenities;

    this.createEmployeesMap();
    this._generateResourceTypeEnum();
  });

  // Get all the resource states for current time for externally shared maps
  fetchAndSaveExternalLiveMapData = task({ drop: true }, async (filters?: LiveMapQueryFilters) => {
    // TODO: The startTime and endTime need to be derived from the API response so that it doesn't depend on state.getOfficeLocationTime

    // const { startTime, endTime } = this._getStartEndTime(filters);

    const queryFilters = {
      floorId: filters?.floorId,
      // startTime: encodeURIComponent(startTime),
      // endTime: encodeURIComponent(endTime),
    };

    // The locationID is not needed for externally shared maps
    this.currentPollingLocationId = null;

    const liveMapDataEndpoint = urlBuilder.maps.getLiveMapV2Url(queryFilters, this.externalShareToken);
    const payload: LiveMapResponse = await this.ajax.request(liveMapDataEndpoint, {
      type: 'GET',
      headers: { Accept: 'application/vnd.api+json' },
    });

    this.resources = payload.resources;
    this.resourceTypes = payload.resourceTypes;
    this.floors = payload.floors;
    this.neighborhoods = payload.neighborhoods;
    this.amenities = payload.amenities;
    this.companyName = payload.companyName;
    this.draftName = payload.draftName;

    this.createEmployeesMap();
    this._generateResourceTypeEnum();
  });

  createEmployeesMap(): void {
    // Creates a map of employees from reservation and assignment information
    const resources: Resource[] = this.getCurrentResources();
    this.employeesMap = resources.reduce(
      (acc: EmployeeMap, resource) => {
        const { reservedForProfileData, assignedProfileData } = resource;
        if (reservedForProfileData && reservedForProfileData.id) {
          const reservationEmployeeId = getIdFromURN(reservedForProfileData.id);
          if (!reservationEmployeeId) {
            return acc;
          }
          acc['withReservations'][reservationEmployeeId] = reservedForProfileData;
        }

        if (assignedProfileData && assignedProfileData.id) {
          const assignmentEmployeeId = getIdFromURN(assignedProfileData.id);
          if (!assignmentEmployeeId) {
            return acc;
          }
          acc['withAssignments'][assignmentEmployeeId] = assignedProfileData;
        }
        return acc;
      },
      { withReservations: {}, withAssignments: {} },
    );
  }

  // Generates an enum of resource types based on the ones included in the response
  _generateResourceTypeEnum(): void {
    this._resourceTypeEnum = Object.fromEntries(
      this.resourceTypes.map((resourceType) => [
        resourceType.id.replace('resource_type:', '').toUpperCase(),
        resourceType.id,
      ]),
    );
  }

  isValidResourceTypeId(id: string): boolean {
    return Object.values(this._resourceTypeEnum).includes(id);
  }

  getEmployeeMap(): EmployeeMap {
    return this.employeesMap;
  }

  getCurrentResources(): Resource[] {
    return this.resources;
  }

  getResourcesByType(resourceTypeId: ResourceTypeId): Resource[] {
    if (!this.isValidResourceTypeId(resourceTypeId)) {
      // eslint-disable-next-line no-console
      console.warn(`Invalid resource type ID: ${resourceTypeId}`);
      return [];
    }

    const currentResources: Resource[] = this.getCurrentResources();

    return currentResources.filter((resource) => resource.resourceTypeId === resourceTypeId);
  }

  getAvailableResourcesByType(resourceTypeId: ResourceTypeId): Resource[] {
    if (!this.isValidResourceTypeId(resourceTypeId)) {
      // eslint-disable-next-line no-console
      console.warn(`Invalid resource type ID: ${resourceTypeId}`);
      return [];
    }

    const currentResources: Resource[] = this.getCurrentResources();
    return currentResources.filter(
      (resource) =>
        resource.resourceTypeId === resourceTypeId && resource.availabilityStatus === AvailabilityStatus.AVAILABLE,
    );
  }

  getResource(resourceId: string, resourceTypeId: ResourceTypeId): Resource | undefined {
    if (!this.isValidResourceTypeId(resourceTypeId)) {
      // eslint-disable-next-line no-console
      console.warn(`Invalid resource type ID: ${resourceTypeId}`);
      return undefined;
    }

    const currentResources: Resource[] = this.getCurrentResources();
    return currentResources.find(
      (resource) => resource.id === resourceId && resource.resourceTypeId === resourceTypeId,
    );
  }

  getResourceAvailability(
    resourceId: string,
    resourceTypeId: ResourceTypeId,
  ): Resource['availabilityStatus'] | undefined {
    if (!this.isValidResourceTypeId(resourceTypeId)) {
      // eslint-disable-next-line no-console
      console.warn(`Invalid resource type ID: ${resourceTypeId}`);
      return undefined;
    }

    return this.getResource(resourceId, resourceTypeId)?.availabilityStatus;
  }

  getAmenityById(id: string): Amenity | undefined {
    return this.amenities.find((amenity) => amenity.id === id);
  }

  async getUpdatedLiveMapData(filters?: LiveMapQueryFilters): Promise<void> {
    this.stopPolling();

    // Fetches the latest live map and starts polling management
    await this.startPollingLiveMap(this.fetchAndSaveLiveMapData, filters);
  }

  async getUpdatedExternalLiveMapData(shareToken: string, filters?: LiveMapQueryFilters): Promise<void> {
    this.stopPolling();
    this.externalShareToken = shareToken;

    // Fetches the latest live map and starts polling management
    await this.startPollingLiveMap(this.fetchAndSaveExternalLiveMapData, filters);
  }

  get currentUsersResources(): Resource[] {
    const currentAdminId = this.state.currentAdmin.employee?.id;
    if (!currentAdminId) return [];

    const resources: Resource[] = this.getCurrentResources();
    return resources.filter(
      (resource: Resource): resource is Resource =>
        resource.reservedForProfileData?.id === currentAdminId || resource.assignedProfileData?.id === currentAdminId,
    );
  }

  stopPolling(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
  // In case a consumer needs to restart the polling cycle
  async restartPollingLiveMap(): Promise<void> {
    this.stopPolling();

    const currentFilters: LiveMapQueryFilters | undefined = this.currentPollingLocationId
      ? this.currentPollingLocationId === this.state?.currentLocation?.id
        ? { locationId: this.currentPollingLocationId }
        : { areaMapId: this.currentPollingLocationId }
      : undefined;

    this.didLiveUpdateFail = false;
    if (this.externalShareToken) {
      await this.startPollingLiveMap(this.fetchAndSaveExternalLiveMapData, currentFilters);
    } else {
      await this.startPollingLiveMap(this.fetchAndSaveLiveMapData, currentFilters);
    }
  }

  willDestroy(): void {
    this.stopPolling();
    super.willDestroy();
  }

  private async startPollingLiveMap(
    taskCallback: TaskForAsyncTaskFunction<
      {
        drop: boolean;
      },
      (filters?: LiveMapQueryFilters) => Promise<void>
    >,
    filters?: LiveMapQueryFilters,
  ): Promise<void> {
    try {
      await taskCallback.perform(filters);

      if (this.didLiveUpdateFail) {
        this.didLiveUpdateFail = false;
      }
    } catch (error) {
      if (!this.didLiveUpdateFail) {
        this.didLiveUpdateFail = true;
      }
      this.flashMessages.showFlash('error', 'Live map data could not be fetched');
      // eslint-disable-next-line no-console
      console.error('Failed to fetch resource states', error);
      if (this.externalShareToken) {
        throw error;
      }
    } finally {
      // Clear existing timeout before setting an ew one
      this.stopPolling();

      this.timeoutId = setTimeout(
        () => void this.startPollingLiveMap(taskCallback, filters),
        POLLING_INTERVAL_MINUTES * 60 * 1000,
      );
    }
  }
}
